<template>
  <v-card flat class="cp-page">

    <!-- ══ HEADER ══ -->
    <div class="d-flex flex-wrap align-center px-3 py-2 teal darken-3 white--text" style="gap: 8px; min-height: 52px;">
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-2" size="20">mdi-cash-multiple</v-icon>
        <span class="text-body-1 font-weight-bold">Collector payments</span>
      </div>
      <div class="flex-spacer"></div>
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search..."
        single-line
        hide-details
        dense
        solo
        flat
        clearable
        background-color="white"
        class="search-field font-weight-medium cp-search"
        style="border-radius: 8px;"
        @input="forcedGroup = null; highlightCollector = ''"
      ></v-text-field>
    </div>

    <!-- ══ FILTER BAR ══ -->
    <div class="d-flex flex-wrap align-center px-2 px-sm-3 py-2 border-bottom" style="gap: 8px; background: #f8fafc;">
      <v-select
        v-model="selectedCycle"
        :items="cycleOptions"
        label="Cycle"
        dense
        outlined
        hide-details
        class="cp-filter-field"
      ></v-select>

      <v-btn
        v-if="selectedCycle !== 'All' || search"
        x-small text color="error"
        @click="resetFilters"
      >
        <v-icon left x-small>mdi-filter-off</v-icon> Reset
      </v-btn>

      <div class="flex-spacer"></div>

      <v-btn x-small text color="teal darken-2" class="font-weight-bold" @click="openGlobalHistoryDialog">
        <v-icon left x-small>mdi-history</v-icon> Global history
      </v-btn>
    </div>

    <!-- ══ TABS ══ -->
    <v-tabs v-model="activeTab" background-color="white" color="teal darken-3" slider-size="3" class="cp-tabs" @change="onTabChange">
      <v-tab href="#pending" class="font-weight-bold">
        To pay
        <span class="cp-count teal darken-3">{{ dueCount }}</span>
      </v-tab>
      <v-tab href="#validated" class="font-weight-bold">
        Paid
        <span class="cp-count green darken-2">{{ paidCount }}</span>
      </v-tab>
    </v-tabs>

    <!-- ══ BODY ══ -->
    <div class="cp-body pa-2 pa-sm-4">

      <!-- Loading -->
      <div v-if="loading" class="text-center pa-8">
        <v-progress-circular indeterminate color="teal" size="40"></v-progress-circular>
        <p class="mt-3 text-caption grey--text">Loading...</p>
      </div>

      <!-- Boot loading : courte animation avant l'ouverture automatique du dialogue -->
      <div v-else-if="dialogBootLoading" class="cp-boot-overlay">
        <div class="cp-boot-card">
          <v-progress-circular indeterminate color="teal" size="34"></v-progress-circular>
          <p class="mt-3 text-caption primary--text font-weight-medium">Preparing payment…</p>
        </div>
      </div>

      <!-- ════ TAB: TO PAY ════ -->
      <template v-else-if="activeTab === 'pending'">

        <div v-if="pendingCards.length === 0" class="text-center pa-8 bg-white rounded-lg elevation-1">
          <v-icon color="success lighten-1" size="56">mdi-check-all</v-icon>
          <p class="mt-2 text-body-2 grey--text">No collector to pay.</p>
        </div>

        <div v-else>
          <div
            v-for="card in pendingCards"
            :key="card.key"
            class="cp-row"
            :class="{ 'cp-row-highlight': isHighlightedCard(card) }"
          >

            <!-- Identity zone -->
            <div class="cp-id">
              <v-avatar size="38" color="teal lighten-4" class="teal--text text--darken-3 font-weight-bold flex-shrink-0">
                {{ renderAvatarName(card) }}
              </v-avatar>

<div class="cp-info">
                  <div class="d-flex align-center" style="gap: 6px;">
                    <span class="cp-title text-truncate">{{ renderName(card) }}</span>
                    <v-chip
                      v-if="card.type === 'current'"
                      x-small color="teal" text-color="white"
                      class="font-weight-bold"
                    >Current cycle</v-chip>
                    <v-chip
                      v-else-if="card.type === 'upcoming'"
                      x-small color="blue" text-color="white"
                      class="font-weight-bold"
                    >In progress</v-chip>
                    <v-chip
                      v-else
                      x-small outlined color="grey lighten-1"
                      class="font-weight-bold"
                    >Previous</v-chip>
                    <v-chip
                      v-if="card.hiddenCount > 0"
                      x-small outlined color="teal"
                      class="font-weight-bold"
                      @click.stop="openUnpaidPastSessionsModal(card.group)"
                    >+{{ card.hiddenCount }} more</v-chip>
                  </div>
                <div class="cp-subtitle">{{ card.group.name }}</div>
                <div class="cp-dates">
                  <v-icon x-small color="grey">mdi-calendar-range</v-icon>
                  {{ renderStartDate(card) }} → {{ renderEndDate(card) }}
                </div>
              </div>
            </div>

            <!-- Action zone -->
            <div class="cp-act">

              <div class="cp-amount">{{ fmt(renderAmount(card)) }} <span class="cp-amount-unit">FCFA</span></div>

              <div class="cp-act-btns">
                <v-btn
                  icon small text color="grey lighten-1"
                  @click.stop="openHistoryDialog(card.group)"
                >
                  <v-icon small>mdi-history</v-icon>
                </v-btn>
                <v-btn
                  :small="$vuetify.breakpoint.xs"
                  depressed rounded color="teal darken-2"
                  dark
                  class="font-weight-bold"
                  style="min-width: 112px;"
                  @click.stop="payCard(card)"
                >
                  <v-icon left x-small>mdi-cash-check</v-icon> Pay
                </v-btn>
              </div>

            </div>

          </div>
        </div>
      </template>

      <!-- ════ TAB: PAID ════ -->
      <template v-else-if="activeTab === 'validated'">

        <div v-if="paidRecords.length === 0" class="text-center pa-8 bg-white rounded-lg elevation-1">
          <v-icon color="grey lighten-1" size="56">mdi-check-circle-outline</v-icon>
          <p class="mt-2 text-body-2 grey--text">No payments recorded.</p>
        </div>

        <div v-else>
          <div
            v-for="h in paidRecords"
            :key="h.id"
            class="cp-row"
            :class="{ 'cp-row-highlight': paidHighlight && h.collector && h.collector.toLowerCase() === paidHighlight.toLowerCase() }"
          >
            <!-- Identity zone -->
            <div class="cp-id">
              <v-avatar size="38" color="green lighten-4" class="green--text text--darken-3 font-weight-bold flex-shrink-0">
                {{ initials(h.collector) }}
              </v-avatar>

              <div class="cp-info">
                <div class="d-flex align-center" style="gap: 6px;">
                  <span class="cp-title text-truncate">{{ h.collector }}</span>
                  <v-chip x-small color="success" text-color="white" class="font-weight-bold">PAID</v-chip>
                </div>
                <div class="cp-subtitle">{{ h.group_name }}</div>
                <div class="cp-dates">
                  <v-icon x-small color="grey">mdi-calendar-check</v-icon>
                  {{ h.paid_at }} · Session #{{ h.session_number }}
                </div>
              </div>
            </div>

            <!-- Action zone -->
            <div class="cp-act">
              <div class="cp-amount green--text text--darken-2 text-right">
                {{ fmt(h.amount_paid) }} <span class="cp-amount-unit">FCFA</span>
              </div>
            </div>

            <div v-if="h.payment_method || h.recorded_by || h.notes" class="cp-extra">
              <span v-if="h.payment_method">Method: <strong>{{ h.payment_method }}</strong></span>
              <span v-if="h.recorded_by">By: <strong>{{ h.recorded_by }}</strong></span>
              <span v-if="h.notes">Notes: <em>{{ h.notes }}</em></span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ══ PAYMENT CONFIRMATION DIALOG ══ -->
    <v-dialog
      v-model="confirmDialog"
      :max-width="$vuetify.breakpoint.smAndUp ? '520' : '100%'"
      :fullscreen="$vuetify.breakpoint.xs"
      persistent
      :content-class="$vuetify.breakpoint.xs ? 'pay-dialog-shell' : 'rounded-xl overflow-hidden'"
    >
      <v-card v-if="selectedGroupForPayment" class="pay-dialog-card">
        <!-- ══ Hero ══ -->
        <div class="pay-hero">
          <div class="pay-hero-bg"></div>
          <v-btn
            icon small dark
            class="pay-close"
            @click="confirmDialog = false"
            :disabled="submittingPayment"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>

          <div class="pay-hero-body">
            <v-avatar size="56" color="white" class="pay-avatar">
              <span class="pay-avatar-text">{{ initials(getActivePaymentCollectorName()) }}</span>
            </v-avatar>

            <div class="pay-hero-name">
              {{ getActivePaymentCollectorName() }}
              <span class="pay-hero-role">Collector</span>
            </div>
            <div class="pay-hero-sub">
              <v-icon x-small>mdi-account-group</v-icon>
              {{ selectedGroupForPayment.name }}
              <span class="pay-hero-dot">·</span>
              {{ selectedGroupForPayment.collecting_cycle }}
            </div>

            <div class="pay-hero-meta">
              <v-chip small dark class="font-weight-bold" style="background: rgba(255,255,255,.18);">
                <v-icon left x-small>mdi-counter</v-icon>
                Session #{{ activePaymentSessionNumber || '—' }}
              </v-chip>
              <v-chip small dark class="font-weight-bold" style="background: rgba(255,255,255,.18);" v-if="activePaymentSessionDates.start">
                <v-icon left x-small>mdi-calendar-range</v-icon>
                {{ activePaymentSessionDates.start }} → {{ activePaymentSessionDates.end }}
              </v-chip>
            </div>
          </div>
        </div>

        <v-card-text class="pay-scroll pa-4 pt-4">

          <!-- Amount -->
          <div class="pay-field-label">
            <v-icon x-small color="teal darken-2" class="mr-1">mdi-cash-check</v-icon>
            Amount sent to collector
          </div>

          <div class="pay-amount-box" :class="{ 'is-filled': customPayAmount > 0 }">
            <v-text-field
              v-model.number="customPayAmount"
              type="number"
              outlined
              dense
              hide-details
              label="0"
              single-line
              class="pay-amount-input"
            >
              <template v-slot:prepend>
                <span class="pay-amount-currency">FCFA</span>
              </template>
            </v-text-field>
            <div class="pay-quick" v-if="selectedGroupForPayment.amount_contributed > 0 && !targetPastSessionObj">
              <v-btn
                x-small outlined color="teal" class="font-weight-bold"
                @click="customPayAmount = selectedGroupForPayment.amount_contributed"
              >Use collected · {{ fmt(selectedGroupForPayment.amount_contributed) }}</v-btn>
            </div>
          </div>

          <div v-if="getActivePaymentAmountExpected() > 0" class="pay-expected-line">
            <v-icon x-small color="grey lighten-1" class="mr-1">mdi-help-circle-outline</v-icon>
            <span>Expected for this cycle</span>
          </div>

          <!-- Method -->
          <div class="pay-field-label mt-3">
            <v-icon x-small color="teal darken-2" class="mr-1">mdi-credit-card-outline</v-icon>
            Payment method
          </div>
          <div class="pay-methods">
            <div
              v-for="m in paymentMethodOptions"
              :key="m"
              class="pay-method"
              :class="{ active: confirmPaymentMethod === m }"
              @click="confirmPaymentMethod = m"
            >
              <v-img
                :src="paymentMethodLogos[m]"
                contain
                class="pay-method-logo"
              ></v-img>
              <span class="pay-method-name">{{ m }}</span>
              <v-icon x-small :color="confirmPaymentMethod === m ? '#00897b' : 'grey lighten-1'">
                {{ confirmPaymentMethod === m ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
            </div>
          </div>

          <!-- Note -->
          <div class="pay-field-label mt-3">
            <v-icon x-small color="teal darken-2" class="mr-1">mdi-note-text-outline</v-icon>
            Note or reference
          </div>
          <v-textarea
            v-model="confirmPaymentNote"
            outlined dense rows="2"
            label="Optional"
            hide-details
            class="pay-note rounded-lg"
          ></v-textarea>

          <div class="pay-hint mt-3 caption grey--text d-flex align-center">
            <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
            Based on the validated contributions. The collector can be notified after payment.
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3 pay-foot">
          <v-btn text color="grey darken-1" @click="confirmDialog = false" :disabled="submittingPayment" rounded>
            Cancel
          </v-btn>
          <div class="flex-spacer"></div>
          <v-btn
            color="teal darken-2"
            rounded
            elevation="2"
            :loading="submittingPayment"
            :disabled="!customPayAmount || customPayAmount <= 0 || !confirmPaymentMethod"
            @click="confirmAndSubmitPayment"
            class="pay-confirm"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Confirm payment
            <span v-if="customPayAmount > 0" class="pay-btn-amount">· {{ fmt(customPayAmount) }} FCFA</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ GROUP HISTORY DIALOG ══ -->
    <v-dialog
      v-model="historyDialog"
      :max-width="$vuetify.breakpoint.smAndUp ? '640' : '100%'"
      :fullscreen="$vuetify.breakpoint.xs"
      scrollable
      content-class="rounded-xl overflow-hidden"
    >
      <v-card v-if="selectedGroupForHistory">
        <v-card-title class="teal darken-3 white--text pa-3 d-flex align-center justify-space-between">
          <div class="d-flex align-center" style="gap: 8px; min-width: 0;">
            <v-icon color="white" small>mdi-history</v-icon>
            <div style="min-width: 0;">
              <div class="text-body-2 font-weight-bold">Payment history</div>
              <div class="caption white--text" style="font-size: 10px; opacity: .8;">
                {{ selectedGroupForHistory.name }} · {{ selectedGroupForHistory.collecting_cycle }}
              </div>
            </div>
          </div>
          <v-btn icon small outlined color="black" @click="historyDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 pa-sm-4" style="max-height: 70vh; overflow-y: auto;">

          <!-- Paid Records -->
          <div class="caption font-weight-bold grey--text text-uppercase mb-2" style="letter-spacing:.05em;">
            <v-icon x-small color="success" class="mr-1">mdi-check-all</v-icon> Records
          </div>
          <div v-if="historyLoading" class="text-center pa-6">
            <v-progress-circular indeterminate color="teal" size="32"></v-progress-circular>
          </div>
          <div v-else-if="!currentGroupHistory || currentGroupHistory.length === 0" class="text-center pa-4 grey--text caption mb-4">
            <v-icon color="grey lighten-1">mdi-history</v-icon>
            <div class="mt-1">No records for this group.</div>
          </div>
          <div v-else class="mb-4">
            <v-card v-for="h in currentGroupHistory" :key="h.id" outlined class="mb-2 pa-3 rounded-lg" style="border-left: 4px solid #00695c;">
              <div class="d-flex align-center justify-space-between" style="gap: 8px;">
                <div class="d-flex align-center" style="gap: 8px; min-width: 0;">
                  <v-avatar size="34" color="teal lighten-4" class="teal--text text--darken-3 font-weight-bold flex-shrink-0">
                    {{ initials(h.collector) }}
                  </v-avatar>
                  <div style="min-width: 0;">
                    <div class="text-subtitle-2 font-weight-bold text--primary text-truncate">{{ h.collector }}</div>
                    <div class="caption grey--text">
                      <v-icon x-small color="grey">mdi-calendar</v-icon> {{ h.collection_date }} · Session #{{ h.session_number }}
                    </div>
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <div class="text-subtitle-2 font-weight-bold success--text">{{ fmt(h.amount_paid) }} FCFA</div>
                  <v-chip x-small color="success" text-color="white" class="font-weight-bold">PAID</v-chip>
                </div>
              </div>
              <div v-if="h.payment_method || h.recorded_by" class="mt-2 pt-2 border-top caption grey--text d-flex flex-wrap" style="gap: 10px; font-size: 11px;">
                <span v-if="h.payment_method">Method: <strong>{{ h.payment_method }}</strong></span>
                <span v-if="h.recorded_by">By: <strong>{{ h.recorded_by }}</strong></span>
                <span v-if="h.notes">Notes: <em>{{ h.notes }}</em></span>
              </div>
            </v-card>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- All Sessions -->
          <div class="caption font-weight-bold grey--text text-uppercase mb-2" style="letter-spacing:.05em;">
            <v-icon x-small color="primary" class="mr-1">mdi-format-list-numbered</v-icon> All sessions
          </div>
          <div v-if="allSessionsLoading" class="text-center pa-6">
            <v-progress-circular indeterminate color="teal" size="32"></v-progress-circular>
          </div>
          <div v-else-if="!allGroupSessions || allGroupSessions.length === 0" class="text-center pa-4 grey--text caption">
            No sessions configured.
          </div>
          <div v-else>
            <v-card
              v-for="s in allGroupSessions" :key="s.session_number"
              outlined class="mb-2 pa-3 rounded-lg"
              :class="{
                'border-left-success': s.already_paid,
                'border-left-warning': s.is_past && !s.already_paid,
                'border-left-primary': s.is_current
              }"
            >
              <div class="d-flex align-start justify-space-between" style="gap: 8px;">
                <div class="d-flex align-start" style="gap: 8px; min-width: 0; flex: 1;">
                  <v-avatar size="34" :color="s.is_current ? 'teal' : s.is_past ? 'grey darken-1' : 'grey lighten-2'"
                    class="white--text font-weight-bold flex-shrink-0 mt-1">
                    #{{ s.position }}
                  </v-avatar>
                  <div style="min-width: 0;">
                    <div class="text-subtitle-2 font-weight-bold text--primary d-flex align-center flex-wrap" style="gap: 4px;">
                      {{ s.collector ? s.collector.username : '—' }}
                      <v-chip v-if="s.is_current" x-small color="primary" text-color="white" class="font-weight-bold">IN PROGRESS</v-chip>
                      <v-chip v-else-if="s.is_past && !s.already_paid" x-small color="warning" text-color="white" class="font-weight-bold">UNPAID</v-chip>
                      <v-chip v-else-if="s.already_paid" x-small color="success" text-color="white" class="font-weight-bold">PAID</v-chip>
                    </div>
                    <div class="caption grey--text">
                      {{ s.session_start_date }} → {{ s.session_end_date }} · {{ fmt(s.amount_expected) }} FCFA
                    </div>
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <div v-if="s.already_paid">
                    <v-chip x-small color="success lighten-5" class="success--text font-weight-bold">
                      <v-icon x-small left color="success">mdi-check-circle</v-icon>
                      Paid ({{ fmt(s.payment_record ? s.payment_record.amount_paid : s.amount_expected) }} FCFA)
                    </v-chip>
                  </div>
                  <v-btn
                    v-else-if="s.is_past || s.is_current"
                    x-small depressed rounded color="warning" dark
                    class="font-weight-bold elevation-1"
                    @click="openMarkPastSessionDialog(s, selectedGroupForHistory)"
                  >
                    <v-icon left x-small>mdi-cash-check</v-icon> Pay
                  </v-btn>
                  <span v-else class="caption grey--text">Upcoming</span>
                </div>
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <div class="flex-spacer"></div>
          <v-btn color="teal darken-2" text rounded @click="historyDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ GLOBAL PAYOUTS HISTORY DIALOG ══ -->
    <v-dialog
      v-model="globalHistoryDialog"
      :max-width="$vuetify.breakpoint.smAndUp ? '600' : '100%'"
      :fullscreen="$vuetify.breakpoint.xs"
      scrollable
      content-class="rounded-xl overflow-hidden"
    >
      <v-card>
        <v-card-title class="teal darken-3 white--text pa-3 d-flex align-center justify-space-between">
          <div class="d-flex align-center" style="gap: 8px;">
            <v-icon color="white" small>mdi-history</v-icon>
            <span class="text-body-2 font-weight-bold">Global history</span>
          </div>
          <v-btn icon small outlined color="black" @click="globalHistoryDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 pa-sm-4" style="max-height: 70vh; overflow-y: auto;">
          <div v-if="globalHistoryLoading" class="text-center pa-8">
            <v-progress-circular indeterminate color="teal" size="40"></v-progress-circular>
          </div>
          <div v-else-if="globalHistory.length === 0" class="text-center pa-8 grey--text">
            <v-icon color="grey lighten-1" size="56">mdi-history</v-icon>
            <p class="mt-2 text-body-2 grey--text">No records.</p>
          </div>
          <div v-else>
            <v-card
              v-for="h in globalHistory"
              :key="h.id"
              outlined class="mb-3 pa-3 rounded-lg"
              style="border-left: 5px solid #00695c;"
            >
              <div class="d-flex align-start justify-space-between flex-wrap" style="gap: 10px;">
                <div class="d-flex align-center" style="gap: 10px; min-width: 0; flex: 1;">
                  <v-avatar size="40" color="teal darken-3" class="white--text font-weight-bold flex-shrink-0">
                    {{ initials(h.collector) }}
                  </v-avatar>
                  <div style="min-width: 0;">
                    <div class="text-subtitle-2 font-weight-bold text--primary d-flex align-center flex-wrap" style="gap: 4px;">
                      <span class="text-truncate">{{ h.collector }}</span>
                      <v-chip x-small color="teal" class="white--text font-weight-bold">{{ h.group_name }}</v-chip>
                    </div>
                    <div class="caption grey--text mt-1">
                      <v-icon x-small color="grey">mdi-calendar-clock</v-icon>
                      Paid on {{ h.paid_at }} · Session #{{ h.session_number }}
                    </div>
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <div class="text-subtitle-1 font-weight-bold success--text">{{ fmt(h.amount_paid) }} FCFA</div>
                  <v-chip x-small color="success" text-color="white" class="font-weight-bold">PAID</v-chip>
                </div>
              </div>
              <div v-if="h.payment_method || h.recorded_by" class="mt-2 pt-2 border-top caption grey--text d-flex flex-wrap" style="gap: 12px; font-size: 11px;">
                <span v-if="h.payment_method">Method: <strong>{{ h.payment_method }}</strong></span>
                <span v-if="h.recorded_by">By: <strong>{{ h.recorded_by }}</strong></span>
                <span v-if="h.notes">Notes: <em>{{ h.notes }}</em></span>
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <div class="flex-spacer"></div>
          <v-btn color="teal darken-2" text rounded @click="globalHistoryDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ UNPAID PAST SESSIONS MODAL ══ -->
    <v-dialog
      v-model="pastSessionsDialog"
      :max-width="$vuetify.breakpoint.smAndUp ? '580' : '100%'"
      :fullscreen="$vuetify.breakpoint.xs"
      scrollable
      content-class="rounded-xl overflow-hidden"
    >
      <v-card v-if="selectedGroupForPastModal">
        <v-card-title class="red darken-3 white--text pa-3 d-flex align-center justify-space-between">
          <div class="d-flex align-center" style="gap: 8px; min-width: 0;">
            <v-icon color="white" small>mdi-alert-circle</v-icon>
            <div style="min-width: 0;">
              <div class="text-body-2 font-weight-bold text-truncate">Unpaid sessions</div>
              <div class="caption white--text" style="font-size: 10px; opacity: .85;">
                {{ selectedGroupForPastModal.name }} · {{ selectedGroupForPastModal.pending_payouts.length }} pending
              </div>
            </div>
          </div>
          <v-btn icon small outlined color="black" @click="pastSessionsDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="pa-3 bg-grey-lighten-4 border-bottom">
          <v-text-field
            v-model="pastSessionsSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search by date, session # or collector..."
            dense outlined hide-details clearable
            class="rounded-lg bg-white"
          ></v-text-field>
        </div>

        <v-card-text class="pa-3 pa-sm-4" style="max-height: 65vh; overflow-y: auto;">
          <div v-if="filteredUnpaidPastSessions.length === 0" class="text-center pa-6 grey--text caption">
            <v-icon color="grey lighten-1">mdi-magnify-minus</v-icon>
            <div class="mt-1">No matching sessions.</div>
          </div>
          <div v-else>
            <v-card
              v-for="p in filteredUnpaidPastSessions"
              :key="p.session_number"
              outlined class="mb-2 pa-3 rounded-lg bg-white"
              style="border-left: 4px solid #d32f2f;"
            >
              <div class="d-flex align-start justify-space-between" style="gap: 10px;">
                <div style="min-width: 0; flex: 1;">
                  <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                    <span class="font-weight-bold text-subtitle-2 text--primary">Session #{{ p.session_number }}</span>
                    <v-chip x-small color="error" class="font-weight-bold white--text">UNPAID</v-chip>
                  </div>
                  <div class="caption grey--text text--darken-2 mt-1">
                    <v-icon x-small color="grey">mdi-account</v-icon>
                    Collector: <strong>{{ p.collector ? p.collector.username : '—' }}</strong>
                  </div>
                  <div class="caption grey--text mt-1">
                    <v-icon x-small color="grey">mdi-calendar-range</v-icon>
                    {{ p.session_start_date }} → {{ p.session_end_date }}
                  </div>
                </div>

                <div class="text-right flex-shrink-0">
                  <div class="font-weight-bold red--text text--darken-3 text-subtitle-2 mb-1">
                    {{ fmt(p.amount_expected) }} FCFA
                  </div>
                  <v-btn
                    small depressed color="warning darken-2" dark
                    class="font-weight-bold rounded-lg elevation-1"
                    @click.stop="openMarkPastSessionDialog(p, selectedGroupForPastModal)"
                  >
                    <v-icon left x-small>mdi-cash-check</v-icon> Pay
                  </v-btn>
                </div>
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <div class="flex-spacer"></div>
          <v-btn color="grey darken-2" text rounded @click="pastSessionsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import axios from 'axios'
import mtnLogo from '@/assets/mtn-mobile-money-logo.png'
import omLogo from '@/assets/om.png'

export default {
  name: 'CollectorPayment',
  data() {
    return {
      paymentMethodLogos: {
        'MTN Mobile Money': mtnLogo,
        'Orange Money': omLogo,
      },
      groups: [],
      loading: false,
      search: '',
      forcedGroup: null,
      highlightCollector: '',
      paidHighlight: '',
      selectedCycle: 'All',
      activeTab: 'pending',

      cycleOptions: ['All', 'Weekly', 'Monthly', 'Daily', 'Custom'],

      confirmDialog: false,
      selectedGroupForPayment: null,
      targetPastSessionObj: null,
      submittingPayment: false,
      dialogBootLoading: false,

      historyDialog: false,
      selectedGroupForHistory: null,
      historyLoading: false,
      currentGroupHistory: [],

      allSessionsLoading: false,
      allGroupSessions: [],
      customPayAmount: null,
      confirmPaymentMethod: 'MTN Mobile Money',
      confirmPaymentNote: '',
      paymentMethodOptions: ['MTN Mobile Money', 'Orange Money'],

      globalHistory: [],
      globalHistoryLoading: false,
      globalHistoryDialog: false,

      pastSessionsDialog: false,
      selectedGroupForPastModal: null,
      pastSessionsSearch: '',
    }
  },

  computed: {
    isWeeklyGrace() {
      const day = new Date().getDay()
      return [2, 3, 4].includes(day)
    },

    isMonthlyGrace() {
      const dom = new Date().getDate()
      return dom >= 11 && dom <= 19
    },

    filteredGroups() {
      const q = (this.search || '').trim().toLowerCase()
      return this.groups.filter(g => {
        if (this.selectedCycle !== 'All' && g.collecting_cycle !== this.selectedCycle) return false
        if (this.forcedGroup && g.name !== this.forcedGroup) return false
        if (q) {
          const nameMatch = g.name && g.name.toLowerCase().includes(q)
          const collMatch = g.collector && g.collector.username && g.collector.username.toLowerCase().includes(q)
          const urgentMatch = g.urgent_collector && g.urgent_collector.username && g.urgent_collector.username.toLowerCase().includes(q)
          const pendingMatch = (g.pending_payouts || []).some(p => p.collector && p.collector.username && p.collector.username.toLowerCase().includes(q))
          const upcomingMatch = (g.upcoming_payouts || []).some(u => u.collector && u.collector.username && u.collector.username.toLowerCase().includes(q))
          if (!nameMatch && !collMatch && !urgentMatch && !pendingMatch && !upcomingMatch) return false
        }
        return true
      })
    },

    pendingGroups() {
      return this.filteredGroups.filter(g => {
        if (!g.urgent_session_number) return false
        return !this.isGroupPaid(g)
      })
    },

    pendingCards() {
      const cards = []
      const MAX_PAST = 1000
      for (const g of this.filteredGroups) {
        if (!g.urgent_session_number) continue
        const unpaidPast = (g.pending_payouts || []).slice()
          .sort((a, b) => b.session_number - a.session_number)
          .filter(p => p.session_number !== g.urgent_session_number)
        const hidden = Math.max(0, unpaidPast.length - MAX_PAST)
        if (!g.urgent_session_paid) {
          cards.push({ group: g, type: 'current', payout: this.buildUrgentPayout(g), key: g.id + '-c-' + g.urgent_session_number, hiddenCount: hidden })
        }
        for (let i = 0; i < Math.min(unpaidPast.length, MAX_PAST); i++) {
          const p = unpaidPast[i]
          cards.push({ group: g, type: 'unpaid', payout: p, key: g.id + '-u-' + p.session_number })
        }
        for (const u of (g.upcoming_payouts || []).slice().sort((a, b) => a.session_number - b.session_number)) {
          cards.push({ group: g, type: 'upcoming', payout: u, key: g.id + '-n-' + u.session_number })
        }
      }
      // Tri par urgence : d'abord les groupes avec le plus d'impayés,
      // puis par session la plus récente, puis les sessions suivantes.
      const rankOf = (g) => (g.pending_payouts || []).length
      return cards.sort((a, b) => {
        const ra = rankOf(a.group), rb = rankOf(b.group)
        if (ra !== rb) return rb - ra
        const sa = a.payout && a.payout.session_number || 0
        const sb = b.payout && b.payout.session_number || 0
        if (a.type === 'upcoming' && b.type !== 'upcoming') return 1
        if (a.type !== 'upcoming' && b.type === 'upcoming') return -1
        return sb - sa
      })
    },

    validatedGroups() {
      return this.filteredGroups.filter(g => this.isGroupPaid(g))
    },

    paidRecords() {
      let list = (this.globalHistory || []).slice()
      if (this.search) {
        const q = this.search.toLowerCase().trim()
        list = list.filter(h => {
          return (
            (h.collector && h.collector.toLowerCase().includes(q)) ||
            (h.group_name && h.group_name.toLowerCase().includes(q)) ||
            String(h.session_number || '').includes(q) ||
            (h.paid_at && h.paid_at.toLowerCase().includes(q))
          )
        })
      }
      return list
    },

    filteredUnpaidPastSessions() {
      if (!this.selectedGroupForPastModal || !this.selectedGroupForPastModal.pending_payouts) return []
      const list = this.selectedGroupForPastModal.pending_payouts
      if (!this.pastSessionsSearch || !this.pastSessionsSearch.trim()) return list
      const q = this.pastSessionsSearch.toLowerCase().trim()
      return list.filter(p => {
        const sessionNumMatch = String(p.session_number).includes(q)
        const collectorMatch = p.collector && p.collector.username && p.collector.username.toLowerCase().includes(q)
        const startDateMatch = p.session_start_date && p.session_start_date.toLowerCase().includes(q)
        const endDateMatch = p.session_end_date && p.session_end_date.toLowerCase().includes(q)
        return sessionNumMatch || collectorMatch || startDateMatch || endDateMatch
      })
    },

    dueCount() {
      return this.pendingCards.length
    },

    paidCount() {
      return this.globalHistory.length
    },

    amountHint() {
      const g = this.selectedGroupForPayment
      if (!g) return ''
      if (!this.targetPastSessionObj && g.amount_contributed > 0) {
        return `Suggested from contributions: ${this.fmt(g.amount_contributed)} FCFA`
      }
      const exp = this.getActivePaymentAmountExpected()
      if (exp > 0) return `Expected amount: ${this.fmt(exp)} FCFA`
      return 'Enter the amount sent to the collector'
    },

    activePaymentSessionNumber() {
      if (this.targetPastSessionObj) return this.targetPastSessionObj.session_number
      const g = this.selectedGroupForPayment
      return (g && g.urgent_session_number) || null
    },

    activePaymentSessionDates() {
      if (this.targetPastSessionObj) {
        return {
          start: this.targetPastSessionObj.session_start_date || '—',
          end: this.targetPastSessionObj.session_end_date || '—',
        }
      }
      const g = this.selectedGroupForPayment
      return {
        start: (g && g.urgent_session_start_date) || (g && g.session_start_date) || '—',
        end: (g && g.urgent_session_end_date) || (g && g.session_end_date) || '—',
      }
    },
  },

  mounted() {
    document.title = 'Manager · Collector Payment'
    this.applyRouteFilters()
    this.loadGroups().then(() => this.autoOpenFromRoute())
    this.loadGlobalHistory()
  },

  methods: {
    onTabChange(tab) {
      if (tab === 'validated') {
        this.loadGlobalHistory()
      }
    },

    fmt(val) {
      if (val === null || val === undefined || val === '') return '—'
      const n = Number(val)
      if (!n) return '—'
      return n.toLocaleString('fr-CM')
    },

    initials(name) {
      if (!name) return '?'
      return name.trim().split(/\s+/).map(w => w[0].toUpperCase()).slice(0, 2).join('')
    },

    cycleColor(cycle) {
      const map = { Daily: '#1565c0', Weekly: '#6a1b9a', Monthly: '#00695c', Custom: '#bf360c' }
      return map[cycle] || '#546e7a'
    },

    resetFilters() {
      this.search = ''
      this.forcedGroup = null
      this.highlightCollector = ''
      this.paidHighlight = ''
      this.selectedCycle = 'All'
    },

    applyRouteFilters() {
      const q = this.$route && this.$route.query ? this.$route.query : {}
      this.search = q.collector ? String(q.collector) : ''
      this.forcedGroup = q.group ? String(q.group) : null
      this.highlightCollector = q.collector ? String(q.collector) : ''
    },

    scrollToHighlighted() {
      if (!this.highlightCollector) return
      this.$nextTick(() => {
        const el = this.$el.querySelector('.cp-row-highlight')
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    isHighlightedCard(card) {
      if (!this.highlightCollector) return false
      try {
        return (this.renderName(card) || '').toLowerCase() === this.highlightCollector.toLowerCase()
      } catch (e) {
        return false
      }
    },

    bootAndOpenDialog(open) {
      if (this.dialogBootLoading) return
      this.dialogBootLoading = true
      setTimeout(() => {
        this.$nextTick(() => {
          try { open() } finally { this.dialogBootLoading = false }
        })
      }, 500)
    },

    autoOpenFromRoute() {
      if (!this.forcedGroup && !this.highlightCollector) return
      const group = this.forcedGroup
        ? this.groups.find(g => g.name === this.forcedGroup)
        : null
      if (!group) return

      const q = (this.highlightCollector || '').toLowerCase()

      // ⚠️ On n'ouvre JAMAIS le dialogue pour un collecteur différent de celui
      // cliqué : on ne cible que les sessions réelles impayées de CE collecteur.
      if (q) {
        const pending = (group.pending_payouts || []).find(
          p => p.collector && p.collector.username && p.collector.username.toLowerCase() === q
        )
        const isUrgentHis = group.urgent_collector && group.urgent_collector.username.toLowerCase() === q

        if (pending && !group.urgent_session_paid &&
            group.urgent_session_number === pending.session_number) {
          this.bootAndOpenDialog(() => this.openMarkPastSessionDialog(pending, group))
          return
        }
        if (isUrgentHis && !group.urgent_session_paid) {
          const urgent = this.buildUrgentPayout(group)
          if (urgent && urgent.session_number && !group.urgent_session_paid) {
            this.bootAndOpenDialog(() => this.openMarkPastSessionDialog(urgent, group))
            return
          }
        }
        if (pending && pending.session_number) {
          this.bootAndOpenDialog(() => this.openMarkPastSessionDialog(pending, group))
          return
        }

        // Aucune session impayée pour ce collecteur.
        // S'il figure déjà dans l'historique des paiements → on bascule sur
        // l'onglet "Paid" et on met sa ligne en évidence (pas de trou apparent).
        const paidHis = (this.globalHistory || []).find(h =>
          h.collector && h.collector.toLowerCase() === q &&
          (!this.forcedGroup || h.group_name === this.forcedGroup)
        )
        if (paidHis) {
          this.paidHighlight = this.highlightCollector
          this.activeTab = 'validated'
          this.snack(`${this.highlightCollector} has already been paid`, 'success')
        } else {
          const upcoming = (group.upcoming_payouts || []).find(
            u => u.collector && u.collector.username && u.collector.username.toLowerCase() === q
          )
          if (upcoming && upcoming.session_number) {
            this.paidHighlight = ''
            this.activeTab = 'pending'
            this.bootAndOpenDialog(() => this.openMarkPastSessionDialog(upcoming, group))
          } else {
            this.snack(`${group.name}: no unpaid session for ${this.highlightCollector}`, 'grey darken-2')
          }
        }
        return
      }

      // Pas de collecteur ciblé : on n'ouvre pas automatiquement, on affiche la liste.
      return
    },

    isGracePeriod(group) {
      if (!group) return false
      if (group.collecting_cycle === 'Weekly') return this.isWeeklyGrace
      if (group.collecting_cycle === 'Monthly') return this.isMonthlyGrace
      return false
    },

    getLastUnpaidPayout(group) {
      if (!group || !group.pending_payouts || group.pending_payouts.length === 0) return null
      return group.pending_payouts[group.pending_payouts.length - 1]
    },

    isGroupPaid(group) {
      if (!group || !group.urgent_session_number) return false
      if (!group.urgent_session_paid) return false
      const unpaidPast = (group.pending_payouts || []).filter(p => p.session_number !== group.urgent_session_number)
      return unpaidPast.length === 0
    },

    isRelevantForManagement(group) {
      const hasUnpaid = !!this.getLastUnpaidPayout(group)
      if (hasUnpaid) return true

      const now = new Date()
      const start = this.parseDate(group.session_start_date)
      const end = this.parseDate(group.session_end_date)

      if (group.collecting_cycle === 'Monthly') {
        return !!end && end.getTime() < now.getTime()
      }
      const sameMonth = (d) => d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
      return (start && sameMonth(start)) || (end && sameMonth(end))
    },

    parseDate(str) {
      if (!str || str === '—') return null
      const d = new Date(str)
      return isNaN(d.getTime()) ? null : d
    },

    buildUrgentPayout(group) {
      if (!group) return null
      return {
        session_number: group.urgent_session_number,
        collector: group.urgent_collector,
        amount_expected: group.amount_expected || 0,
        session_start_date: group.urgent_session_start_date || '—',
        session_end_date: group.urgent_session_end_date || '—',
      }
    },

    renderAvatarName(card) {
      const coll = card.payout && card.payout.collector
      return this.initials(coll ? coll.username : '')
    },

    renderStartDate(card) {
      return (card.payout && card.payout.session_start_date) || card.group.urgent_session_start_date || card.group.session_start_date || '—'
    },

    renderEndDate(card) {
      return (card.payout && card.payout.session_end_date) || card.group.urgent_session_end_date || card.group.session_end_date || '—'
    },

    renderName(card) {
      const coll = card.payout && card.payout.collector
      return (coll && coll.username) || (card.group.urgent_collector && card.group.urgent_collector.username) || '—'
    },

    renderAmount(card) {
      const val = card.payout ? card.payout.amount_expected : card.group.amount_expected
      return val || 0
    },

    payCard(card) {
      const session = card.type === 'current' ? this.buildUrgentPayout(card.group) : card.payout
      if (!session || !session.session_number) return
      this.openMarkPastSessionDialog(session, card.group)
    },

    handlePrimaryPayClick(group) {
      if (this.isGracePeriod(group)) {
        const lastUnpaid = this.getLastUnpaidPayout(group)
        if (lastUnpaid) {
          this.openMarkPastSessionDialog(lastUnpaid, group)
          return
        }
      }
      this.openPaymentConfirmDialog(group)
    },

    openPaymentConfirmDialog(group) {
      this.selectedGroupForPayment = group
      this.targetPastSessionObj = null
      this.customPayAmount = group.amount_contributed > 0
        ? group.amount_contributed
        : (group.amount_expected > 0 ? group.amount_expected : null)
      this.confirmPaymentMethod = 'MTN Mobile Money'
      this.confirmPaymentNote = ''
      this.confirmDialog = true
    },

    openMarkPastSessionDialog(session, group) {
      this.selectedGroupForPayment = group
      this.targetPastSessionObj = session
      this.customPayAmount = session.amount_expected || null
      this.confirmPaymentMethod = 'MTN Mobile Money'
      this.confirmPaymentNote = ''
      if (this.pastSessionsDialog) this.pastSessionsDialog = false
      this.confirmDialog = true
    },

    getActivePaymentCollectorName() {
      if (this.targetPastSessionObj && this.targetPastSessionObj.collector) {
        return this.targetPastSessionObj.collector.username
      }
      const g = this.selectedGroupForPayment
      return g && g.collector ? g.collector.username : 'Collector'
    },

    getActivePaymentAmountExpected() {
      if (this.targetPastSessionObj) return this.targetPastSessionObj.amount_expected || 0
      return this.selectedGroupForPayment ? (this.selectedGroupForPayment.amount_expected || 0) : 0
    },

    async confirmAndSubmitPayment() {
      if (!this.selectedGroupForPayment) return
      if (!this.customPayAmount || this.customPayAmount <= 0) return

      const group = this.selectedGroupForPayment
      const isPastSession = !!this.targetPastSessionObj
      this.submittingPayment = true

      try {
        if (isPastSession) {
          const session = this.targetPastSessionObj
          const payload = {
            session_number: session.session_number,
            amount_paid: this.customPayAmount,
            payment_method: this.confirmPaymentMethod,
            notes: this.confirmPaymentNote
          }
          const res = await axios.post(`/api/v1/manager/njangi/groups/${group.id}/mark-specific-session-paid/`, payload)
          const data = res.data[0]
          this.snack(data.text, data.color)
        } else {
          const formData = new FormData()
          formData.append('amount_paid', this.customPayAmount)
          formData.append('payment_method', this.confirmPaymentMethod)
          formData.append('notes', this.confirmPaymentNote)

          const res = await axios.post(`/api/v1/manager/njangi/groups/${group.id}/mark-collector-paid/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          const data = res.data[0]
          this.snack(data.text, data.color)
        }

        this.confirmDialog = false
        await Promise.all([this.loadGroups(), this.loadGlobalHistory()])
        if (this.selectedGroupForHistory && this.selectedGroupForHistory.id === group.id) {
          await Promise.all([this.loadHistory(group.id), this.loadAllSessions(group.id)])
        }

      } catch (error) {
        console.error('Error submitting payment:', error)
        this.snack('Error while saving payment', 'red darken-4')
      } finally {
        this.submittingPayment = false
      }
    },

    async loadGroups() {
      this.loading = true
      try {
        const res = await axios.get('/api/v1/manager/njangi/manager/groups/')
        this.groups = res.data
        this.scrollToHighlighted()
      } catch {
        this.snack('Failed to load groups', 'red darken-4')
      } finally {
        this.loading = false
      }
    },

    openGlobalHistoryDialog() {
      this.globalHistoryDialog = true
      this.loadGlobalHistory()
    },

    openUnpaidPastSessionsModal(group) {
      this.selectedGroupForPastModal = group
      this.pastSessionsSearch = ''
      this.pastSessionsDialog = true
    },

    async openHistoryDialog(group) {
      this.selectedGroupForHistory = group
      this.historyDialog = true
      await Promise.all([this.loadHistory(group.id), this.loadAllSessions(group.id)])
    },

    async loadHistory(groupId) {
      this.historyLoading = true
      try {
        const res = await axios.get(`/api/v1/manager/njangi/groups/${groupId}/collector-payment-history/`)
        this.currentGroupHistory = res.data
      } catch {
        this.currentGroupHistory = []
      } finally {
        this.historyLoading = false
      }
    },

    async loadAllSessions(groupId) {
      this.allSessionsLoading = true
      try {
        const res = await axios.get(`/api/v1/manager/njangi/groups/${groupId}/all-collector-sessions/`)
        this.allGroupSessions = res.data.sessions || []
      } catch {
        this.allGroupSessions = []
      } finally {
        this.allSessionsLoading = false
      }
    },

    async loadGlobalHistory() {
      this.globalHistoryLoading = true
      try {
        const res = await axios.get('/api/v1/manager/njangi/collector-payments-history/')
        this.globalHistory = res.data
      } catch {
        this.snack('Failed to load history', 'red darken-4')
      } finally {
        this.globalHistoryLoading = false
      }
    },

    snack(msg, color = 'success') {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('setSnackBarTimeOut', 5000)
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.cp-page { background: #f8fafc; min-height: 100vh; max-width: 100vw; overflow-x: hidden; }

.cp-boot-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 45vh;
}
.cp-boot-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 44px;
  background: transparent;
  border-radius: 16px;
}

.border-bottom { border-bottom: 1px solid #e2e8f0 !important; }

/* ── Tabs ── */
.cp-tabs { border-bottom: 2px solid #e2e8f0; }
.cp-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  margin-left: 7px;
  padding: 0 6px;
}

/* ── Flex spacer ── */
.flex-spacer { flex: 1 1 auto; }

/* ── Filter select ── */
.cp-filter-field {
  flex: 1 1 160px;
  min-width: 140px;
  max-width: 240px;
}

/* ── Header search ── */
.cp-search { max-width: 200px; min-width: 120px; }

/* ── Row (list item) ── */
.cp-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: white;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  transition: box-shadow .15s ease;
}
.cp-row-highlight {
  outline: 2px solid #00897b;
  background: #e0f2f1 !important;
  box-shadow: 0 2px 10px rgba(0, 137, 123, 0.25);
}
@media (hover: hover) {
  .cp-row:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
}

.cp-id {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 240px;
  min-width: 0;
}

.cp-info { flex: 1 1 auto; min-width: 0; }

.cp-act {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
}

.cp-act-btns {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cp-title {
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
}

.cp-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.cp-extra {
  flex-basis: 100%;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: #64748b;
  border-top: 1px dashed #e2e8f0;
  padding-top: 8px;
}

.cp-dates {
  font-size: 11px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  white-space: nowrap;
}

.cp-session {
  font-weight: 700;
  color: #0f766e;
  background: #e6fffa;
  border-radius: 4px;
  padding: 0 4px;
}

.cp-amount {
  font-weight: 700;
  font-size: 14px;
  color: #0f766e;
  white-space: nowrap;
}

.cp-amount-unit {
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
}

/* ── Mobile (≤ 600px) ── */
@media (max-width: 600px) {
  .cp-search {
    flex: 1 1 100%;
    max-width: 100% !important;
    min-width: 0;
  }

  .cp-filter-field {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .cp-row { padding: 14px; gap: 10px; }

  .cp-id { flex-basis: 100%; }

  .cp-act {
    width: 100%;
    margin-left: 0;
    justify-content: space-between;
    border-top: 1px solid #eef2f7;
    padding-top: 10px;
  }

  .cp-title { font-size: 16px; }
  .cp-subtitle { font-size: 13px; }
  .cp-dates { font-size: 12.5px; }
  .cp-amount { font-size: 17px; }
  .cp-amount-unit { font-size: 11px; }
  .cp-count { font-size: 12px; min-width: 22px; height: 22px; }
}

.search-field >>> .v-input__control { min-height: 36px !important; }
.search-field >>> input { color: #1e293b !important; font-size: 13px !important; }
.search-field >>> .v-input__slot {
  box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important;
  border: 1px solid #cbd5e1 !important;
}

/* ══ PAYMENT DIALOG ══ */
.pay-dialog-card { overflow: hidden; }
.pay-dialog-card >>> .v-card__text { padding: 16px !important; }
.pay-scroll { overflow-y: auto; max-height: calc(100vh - 180px); }

.pay-hero {
  position: relative;
  overflow: hidden;
  padding: 20px 16px 16px;
  background: linear-gradient(135deg, #00897b 0%, #00695c 60%, #004d40 100%);
  text-align: center;
  color: #fff;
}
.pay-hero-bg {
  position: absolute;
  right: -40px; top: -40px;
  width: 160px; height: 160px;
  border-radius: 50%;
  background: rgba(255,255,255,.08);
}
.pay-hero-bg::after {
  content: '';
  position: absolute;
  left: -70px; bottom: -70px;
  width: 180px; height: 180px;
  border-radius: 50%;
  background: rgba(255,255,255,.06);
}
.pay-close { position: absolute; right: 6px; top: 6px; z-index: 2; }
.pay-hero-body { position: relative; z-index: 1; }
.pay-avatar {
  box-shadow: 0 2px 10px rgba(0,0,0,.25);
  margin: 0 auto 8px;
}
.pay-avatar-text {
  font-weight: 700;
  font-size: 20px;
  color: #004d40;
}
.pay-hero-name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: .2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.pay-hero-role {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  background: rgba(255,255,255,.2);
  border-radius: 999px;
  padding: 2px 8px;
}
.pay-hero-sub {
  font-size: 12px;
  opacity: .92;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 2px;
}
.pay-hero-dot { opacity: .6; }
.pay-hero-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.pay-field-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #64748b;
  margin-bottom: 6px;
}

.pay-amount-box {
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  transition: border-color .2s ease, box-shadow .2s ease;
}
.pay-amount-box.is-filled {
  border-color: #00897b;
  box-shadow: 0 0 0 3px rgba(0,137,123,.12);
  background: #fff;
}
.pay-amount-input >>> .v-input__slot { background: transparent !important; box-shadow: none !important; border: none !important; }
.pay-amount-input >>> input { font-size: 22px !important; font-weight: 700 !important; color: #1e293b !important; }
.pay-amount-currency { font-weight: 700; font-size: 13px; color: #00897b; }
.pay-quick { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }

.pay-expected-line {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  margin-top: 8px;
}

.pay-methods { display: flex; gap: 10px; }
.pay-method {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 8px;
  border: 1.5px solid #cbd5e1;
  border-radius: 12px;
  cursor: pointer;
  transition: all .18s ease;
  background: #f8fafc;
}
.pay-method:hover { border-color: #94a3b8; background: #f1f5f9; }
.pay-method.active {
  border-color: #00897b;
  background: rgba(0,137,123,.06);
  box-shadow: 0 0 0 3px rgba(0,137,123,.12);
}
.pay-method-logo { width: 34px; height: 30px; flex-shrink: 0; filter: drop-shadow(0 1px 1px rgba(0,0,0,.12)); }
.pay-method-name { font-size: 12px; font-weight: 600; color: #1e293b; }
.pay-note >>> .v-input__slot { box-shadow: none !important; border: 1px solid #cbd5e1 !important; }

.pay-hint { line-height: 1.4; }

.pay-btn-amount { font-weight: 600; opacity: .92; }

.pay-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.pay-confirm {
  min-width: 220px;
  flex: 1 1 auto;
  font-weight: 700;
  letter-spacing: .2px;
  border-radius: 12px !important;
  background: linear-gradient(135deg, #26a69a 0%, #00796b 60%, #00695c 100%) !important;
  box-shadow: 0 4px 14px rgba(0, 121, 107, .35) !important;
  transition: transform .18s ease, box-shadow .18s ease !important;
}
.pay-confirm:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 121, 107, .42) !important;
}
.pay-confirm:active:not(:disabled) { transform: translateY(0); box-shadow: 0 2px 8px rgba(0,121,107,.3) !important; }
.pay-confirm.v-btn--disabled { opacity: .55 !important; }
@media (max-width: 600px) {
  .pay-confirm { width: 100% !important; flex: 1 1 100%; min-width: 0; }
  .pay-foot .flex-spacer { display: none; }
}

/* ── Payment dialog: mobile tuning ── */
@media (max-width: 600px) {
  .pay-dialog-shell { width: 100% !important; max-width: 100% !important; }
  .pay-dialog-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: 0 !important;
  }
  .pay-dialog-card .pay-hero { flex-shrink: 0; }
  .pay-dialog-card .pay-foot { flex-shrink: 0; }
  .pay-dialog-card >>> .pay-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    max-height: none !important;
    -webkit-overflow-scrolling: touch;
  }
  .pay-dialog-card >>> .v-card__text { padding: 14px !important; }
  .pay-hero { padding: 12px 14px 12px; }
  .pay-hero-name { font-size: 15px; }
  .pay-hero-sub { font-size: 11px; flex-wrap: wrap; }
  .pay-hero-meta { gap: 6px; margin-top: 8px; }
  .pay-avatar { width: 42px !important; height: 42px !important; }
  .pay-avatar-text { font-size: 15px; }
  .pay-amount-box { padding: 8px 10px; }
  .pay-amount-input >>> input { font-size: 18px !important; }
  .pay-quick .v-btn { flex: 1 1 100%; }
  .pay-methods { flex-direction: column; gap: 8px; }
  .pay-method { justify-content: flex-start; padding: 10px 12px; }
  .pay-method-logo { width: 40px; height: 32px; }
  .pay-foot { flex-direction: column-reverse; padding: 10px 12px; }
  .pay-foot .v-btn { width: 100%; flex: 1 1 auto; min-width: 0; margin: 0 !important; }
  .pay-foot .flex-spacer { display: none; }
}
</style>